import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/api/client'

export type PickupPaymentKind = 'CARD' | 'VOUCHER' | 'FORKS'

export interface PickupPaymentPart {
  kind: PickupPaymentKind
  /** Voucher type name; null for card and forks. */
  label: string | null
  count: number
  captured: boolean
}

/** Server-normalized: 'WARM' | 'COLD', or null when the product offers no choice. */
export type PickupTemperature = 'WARM' | 'COLD' | null

export interface PickupItem {
  name: string
  quantity: number
  temperature: PickupTemperature
}

export interface PickupOrderRow {
  orderId: string
  restaurantId: number
  restaurantName: string
  pickupAt: string
  pickupCode: string | null
  /** Null for a group order, which has no single customer. */
  customerName: string | null
  status: string
  totalGrossCents: number
  /** False whenever money is still due or a voucher is unverified — cannot be handed over. */
  settled: boolean
  payments: Array<PickupPaymentPart>
  items: Array<PickupItem>
}

export interface PickupReport {
  date: string
  generatedAt: string
  timezone: string
  /** Null in all-restaurants mode. */
  restaurant: { id: number; name: string; code: string } | null
  truncated: boolean
  totals: { orderCount: number; dishCount: number; totalGrossCents: number }
  dishTotals: Array<{
    name: string
    quantity: number
    temperature: PickupTemperature
  }>
  orders: Array<PickupOrderRow>
}

export function usePickupsQuery(
  date: string,
  restaurantId: number | null,
  enabled = true,
) {
  const params = new URLSearchParams({ date })
  if (restaurantId != null) params.set('restaurantId', String(restaurantId))
  const qs = params.toString()

  return useQuery({
    queryKey: ['pickups', date, restaurantId],
    queryFn: () => apiRequest<PickupReport>(`/api/v1/admin/pickups?${qs}`),
    // A report is a snapshot the user refreshes deliberately; the "Stand" line states when.
    staleTime: Infinity,
    enabled: enabled && date !== '',
  })
}
